import { Component, OnInit } from '@angular/core';
import { CompanyService, CompanyInfo, BusinessHour } from '../../services/company.service';
import { MetaTagService } from '../../services/meta-tag.service';

@Component({
    selector: 'app-about',
    templateUrl: './about.component.html',
    styleUrls: ['./about.component.scss'],
    standalone: false
})
export class AboutComponent implements OnInit {

  company: CompanyInfo | null = null;
  loading = true;
  error = false;

  /** Israeli week order for sorting */
  private readonly dayOrder = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  /** FA4-compatible icon map per platform */
  readonly socialIconMap: Record<string, { icon: string; color: string }> = {
    instagram: { icon: 'fa-instagram',  color: '#E1306C' },
    facebook:  { icon: 'fa-facebook',   color: '#1877F2' },
    tiktok:    { icon: 'fa-music',       color: '#111827' },  // FA4 has no tiktok, use music
    youtube:   { icon: 'fa-youtube',    color: '#FF0000' },
    linkedin:  { icon: 'fa-linkedin',   color: '#0A66C2' },
    twitter:   { icon: 'fa-twitter',    color: '#111827' },
    whatsapp:  { icon: 'fa-whatsapp',   color: '#25D366' },
    telegram:  { icon: 'fa-paper-plane',color: '#2AABEE' }, // FA4 has no telegram
  };

  constructor(private companyService: CompanyService, private seo: MetaTagService) {}

  ngOnInit(): void {
    this.seo.update({
      title:       'אודותינו',
      description: `הכירו את ${this.seo.siteName} — פלטפורמת המכירות המובילה בישראל. למדו עלינו, על הצוות ועל החזון שלנו.`,
      keywords:    `אודות ${this.seo.siteName}, פלטפורמת מכירות ישראל, מי אנחנו`,
      url:         this.seo.pageUrl('/about'),
      jsonLd: {
        '@context': 'https://schema.org',
        '@type':    'AboutPage',
        'name':     `אודות ${this.seo.siteName}`,
        'url':      this.seo.pageUrl('/about'),
      }
    });
    this.companyService.getCompanyInfo().subscribe({
      next: (data) => {
        // Sort business hours in Israeli week order
        data.business_hours = data.business_hours.sort((a, b) =>
          this.dayOrder.indexOf(a.day) - this.dayOrder.indexOf(b.day)
        );
        this.company = data;
        this.loading = false;
      },
      error: () => {
        this.error = true;
        this.loading = false;
      }
    });
  }

  get todayDayName(): string {
    return this.dayOrder[new Date().getDay()];
  }

  isToday(hour: BusinessHour): boolean {
    return hour.day === this.todayDayName;
  }

  hoursLabel(hour: BusinessHour): string {
    if (hour.is_closed) return 'סגור';
    const fmt = (t: string | null) => {
      if (!t) return '';
      const parts = t.split(':');
      return parts.length >= 2 ? `${parts[0]}:${parts[1]}` : t;
    };
    const o = fmt(hour.open_time);
    const c = fmt(hour.close_time);
    if (!o && !c) return '—';
    return `${o} – ${c}`;
  }

  formatPhone(raw: string): string {
    let digits = raw.replace(/[\s\-()+]/g, '');
    if (digits.startsWith('972') && digits.length >= 10) {
      digits = '0' + digits.substring(3);
    }
    if (digits.length === 10) {
      return `${digits.substring(0, 3)}-${digits.substring(3, 6)}-${digits.substring(6)}`;
    }
    return raw;
  }

  socialCfg(platform: string): { icon: string; color: string } {
    return this.socialIconMap[platform.toLowerCase()] || { icon: 'fa-globe', color: '#6B7280' };
  }

  openLink(url: string): void {
    if (url) { window.open(url, '_blank'); }
  }

  callPhone(phone: string): void {
    window.location.href = `tel:${phone}`;
  }

  openEmail(email: string): void {
    window.location.href = `mailto:${email}`;
  }

  openWhatsapp(whatsapp: string): void {
    const digits = whatsapp.replace(/\D/g, '');
    window.open(`https://wa.me/${digits}`, '_blank');
  }

  openMaps(address: string, city: string): void {
    const query = encodeURIComponent(`${address}, ${city}`);
    window.open(`https://maps.google.com/?q=${query}`, '_blank');
  }
}
